import React, { useState } from 'react';
import { Button, Typography, Card } from 'antd';
import { MessageOutlined, AimOutlined, LockOutlined, HistoryOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { ProjectStage, STAGE_GOALS, STAGE_NAMES, STAGE_DESCRIPTIONS } from '../../constants/stages';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Project } from '../../types/project';
import AuthModal from '../auth/AuthModal';
import CreateProjectModal from '../common/CreateProjectModal';
import { selectUser } from '../../store/userSlice';
import BetaBanner from '../common/BetaBanner';
import StageGoals from '../common/StageGoals';

const { Title, Text } = Typography;

const Container = styled.div`
  height: 100%;
  display: flex;
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
  gap: 32px;
  position: relative;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Sidebar = styled.div`
  width: 360px;
  flex-shrink: 0;
  position: sticky;
  top: 80px;
  align-self: flex-start;
  margin-top: 48px;
`;

const StageCard = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  background: #fff;

  .ant-card-body {
    padding: 24px;
  }
`;

const OverviewCard = styled(StageCard)`
  .ant-typography {
    color: #666;
    font-size: 15px;
    line-height: 1.8;
  }
`;

const GoalsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const GoalItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GoalHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const GoalCircle = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid #d9d9d9;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  .anticon {
    font-size: 12px;
    color: #8c8c8c;
  }
`;

const GoalTitle = styled(Text)`
  font-weight: 500;
  color: #1f2937;
  font-size: 15px;
  line-height: 1.6;
`;

const GoalDescription = styled(Text)`
  color: #666;
  padding-left: 32px;
  line-height: 1.6;
`;

const MiddleSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
`;

const StartButton = styled(Button)`
  width: 240px;
  height: 48px;
  font-size: 16px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  .anticon {
    font-size: 18px;
  }
`;

const BottomSection = styled(Card)`
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  text-align: center;
  padding: 24px;

  .ant-card-body {
    padding: 0;
  }
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;

  .anticon {
    font-size: 20px;
    color: #1890ff;
  }

  h4.ant-typography {
    margin: 0;
  }
`;

const SummaryPlaceholder = styled.div`
  color: #666;
  font-size: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  .anticon {
    font-size: 24px;
    color: #1890ff;
  }
`;

const StageTitle = styled(Title)`
  margin: 0 !important;
  color: #1f2937;
`;

const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
  
  .anticon {
    font-size: 20px;
    color: #1890ff;
  }
`;

const DisabledButton = styled(StartButton)`
  &.ant-btn-primary[disabled] {
    background: #e5e7eb;
    border-color: #e5e7eb;
    color: #9ca3af;
    
    .anticon {
      color: #9ca3af;
    }
  }
`;

interface StageOverviewProps {
  stage: ProjectStage;
  currentProject: string;
}

const StageOverview: React.FC<StageOverviewProps> = ({ stage, currentProject }) => {
  const navigate = useNavigate();
  const stageGoals = STAGE_GOALS[stage];
  const stageName = `${STAGE_NAMES[stage]}阶段`;
  const stageDescription = STAGE_DESCRIPTIONS[stage];
  const user = useSelector(selectUser);
  const projects = useSelector((state: RootState) => state.project.projects);
  
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [createProjectModalVisible, setCreateProjectModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  const project = useSelector((state: RootState) => 
    state.project.projects.find((p: Project) => p.id === currentProject)
  );

  const isStageAccessible = () => {
    if (projects.length === 0) return true;
    if (!project) return false;
    const stageIndex = Object.values(ProjectStage).indexOf(stage);
    const currentStageIndex = Object.values(ProjectStage).indexOf(project.currentStage);
    return stageIndex <= currentStageIndex;
  };

  const isStageCompleted = () => {
    if (projects.length === 0) return false;
    if (!project) return false;
    const stageIndex = Object.values(ProjectStage).indexOf(stage);
    const currentStageIndex = Object.values(ProjectStage).indexOf(project.currentStage);
    return stageIndex < currentStageIndex;
  };

  const handleStartChat = () => {
    if (!user) {
      setAuthModalVisible(true);
      return;
    }

    if (projects.length === 0) {
      setCreateProjectModalVisible(true);
      return;
    }

    navigate(`/app/chat/${currentProject}/${stage}`);
  };

  const handleAuthSuccess = () => {
    window.location.reload();
  };

  const handleCreateProject = async (values: { projectName: string; description: string }) => {
    try {
      setLoading(true);
      // TODO: 调用创建项目的 API
      // await createProject(values);
      setCreateProjectModalVisible(false);
      window.location.reload();
    } catch (error) {
      console.error('Failed to create project:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderButton = () => {
    if (!user) {
      return (
        <DisabledButton 
          type="primary"
          icon={<MessageOutlined />}
          onClick={handleStartChat}
          size="large"
        >
          登录以继续
        </DisabledButton>
      );
    }

    if (projects.length === 0) {
      return (
        <DisabledButton 
          type="primary"
          icon={<LockOutlined />}
          disabled
          size="large"
          onClick={handleStartChat}
        >
          请先创建项目
        </DisabledButton>
      );
    }

    if (isStageCompleted()) {
      return (
        <StartButton
          type="primary"
          icon={<HistoryOutlined />}
          onClick={handleStartChat}
          size="large"
        >
          查看对话记录
        </StartButton>
      );
    }

    if (!isStageAccessible()) {
      return (
        <DisabledButton 
          type="primary"
          icon={<LockOutlined />}
          disabled
          size="large"
        >
          请先完成之前阶段
        </DisabledButton>
      );
    }

    return (
      <StartButton
        type="primary"
        icon={<MessageOutlined />}
        onClick={handleStartChat}
        size="large"
      >
        进入该阶段
      </StartButton>
    );
  };

  return (
    <>
      <BetaBanner 
        visible={showBanner}
        onClose={() => setShowBanner(false)}
      />
      <Container>
        <MainContent>
          <StageTitle level={3}>{stageName}</StageTitle>
          
          <OverviewCard>
            <Text>{stageDescription}</Text>
          </OverviewCard>

          <MiddleSection>
            {renderButton()}
          </MiddleSection>

          <BottomSection>
            <SummaryHeader>
              <MessageOutlined />
              <Title level={4}>总结</Title>
            </SummaryHeader>
            <SummaryPlaceholder>
              <Text>对话结束后，AI 助手将为你生成本阶段的工作总结</Text>
            </SummaryPlaceholder>
          </BottomSection>
        </MainContent>

        <Sidebar>
          <StageCard>
            <CardTitle>
              <AimOutlined />
              <Title level={4} style={{ margin: 0 }}>阶段目标</Title>
            </CardTitle>
            <StageGoals stage={stage} projectId={currentProject} />
          </StageCard>
        </Sidebar>
      </Container>

      <AuthModal
        visible={authModalVisible}
        onClose={() => setAuthModalVisible(false)}
        defaultTab="login"
        onSuccess={handleAuthSuccess}
      />

      <CreateProjectModal
        open={createProjectModalVisible}
        onCancel={() => setCreateProjectModalVisible(false)}
        onSubmit={handleCreateProject}
        loading={loading}
        projects={projects}
      />
    </>
  );
};

export default StageOverview; 